import React from 'react';

import { ProfessionalInfo, VerificationInfo } from '@/types/types';
import {
    Control,
    Controller,
    FieldError,
    FieldErrorsImpl,
    FieldValues,
    Merge,
    useForm,
} from 'react-hook-form';
import { Stack, TextField, Typography } from '@mui/material';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { VERIFICATION_PROFESSIONAL_INFO_PHRASES } from '@/consts/phrases/verificationPhrases';
import { SkeletonBlock } from '@/components/general/skeletonBlock/SkeletonBlock';

interface ProfessionalInfoFormProps {
    requestToken: string;
    verificationInfo: VerificationInfo;
    control: ReturnType<typeof useForm>['control'];
    isEnabled: boolean;
    errors: ReturnType<typeof useForm>['formState']['errors'];
}

const FIELD_PREFIX = 'professional-info-';

export const ProfessionalInfoForm = (props: ProfessionalInfoFormProps) => {
    const { professionalInfo } = props.verificationInfo;
    const { t } = usePhrase();

    return (
        <>
            <Typography variant="h5" sx={{ pb: 1 }}>
                {t(VERIFICATION_PROFESSIONAL_INFO_PHRASES.TITLE)}
            </Typography>
            <form>
                <Stack spacing={2} sx={{ mt: 3 }}>
                    {professionalInfo.map((professionalInfoItem, index) => (
                        <div key={index}>
                            <TextFieldController
                                control={props.control}
                                professionalInfo={professionalInfoItem}
                                enabled={props.isEnabled}
                                error={props.errors[professionalInfoItem.label]}
                            />
                        </div>
                    ))}
                </Stack>
            </form>
        </>
    );
};

const TextFieldController = (props: {
    control: Control<FieldValues, unknown, unknown>;
    professionalInfo: ProfessionalInfo;
    enabled: boolean;
    error: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>> | undefined;
}) => (
    <Controller
        name={`${FIELD_PREFIX}${props.professionalInfo.id}`}
        control={props.control}
        render={({ field }) => (
            <TextField
                disabled={!props.enabled}
                fullWidth
                label={props.professionalInfo.label}
                required={props.professionalInfo.required}
                error={Boolean(props.error)}
                helperText={(props.error?.message as string) || ''}
                {...field}
            />
        )}
    />
);

export const PersonalInfoFormSkeleton = () => <SkeletonBlock amount={1} height={60} />;
